/**
 * Cliente del payments-api (el "scrapper") para la verificación biométrica.
 *
 * El widget habla DIRECTO con el payments-api — no vía el backend de lpay.
 * Ese servicio ya expone el estado del payment y la URL de KYC externa, su CORS
 * permite el origen del widget, y es el mismo patrón que OTP/captcha (endpoints
 * interactivos a mitad de run). Acá sólo LEEMOS estado; no enviamos secretos.
 *
 * Conocer el `paymentId` es la única capacidad necesaria (igual que OTP).
 */

/**
 * Estados internos del payment. Lista abierta (`string & {}`) para tolerar
 * estados nuevos del backend sin romper el type ni la UI.
 */
export type PaymentStatus =
  | 'CREATED'
  | 'AUTOMATION_STARTED'
  | 'AWAITING_OTP'
  | 'OTP_RECEIVED'
  | 'AT_WALLET'
  | 'WALLET_FILLED'
  | 'AT_CARD'
  | 'AT_CONFIRM'
  | 'AT_KYC_PERSONAL'
  | 'AT_KYC_ADDRESS'
  | 'AT_KYC_PURPOSE'
  | 'AWAITING_BIOMETRIC'
  | 'KYC_VERIFIED'
  | 'CONFIRMED'
  | 'ORDER_CAPTURED'
  | 'DONE'
  | 'FAILED_OTP_TIMEOUT'
  | 'FAILED_OTP_REJECTED'
  | 'FAILED_KYC_TIMEOUT'
  | 'FAILED_KYC_REJECTED'
  | 'FAILED_CAPTCHA_TIMEOUT'
  | 'FAILED_PAYMENT'
  | 'FAILED_INFRA'
  | 'FAILED_MAX_ATTEMPTS'
  | 'FAILED_BROWSER_CRASH'
  | 'CANCELED'
  | (string & {});

/** Subset de `GetPaymentResponse` que usa la UI biométrica. */
export interface PaymentSnapshot {
  id: string;
  status: PaymentStatus;
  errorCode: string | null;
  errorMessage: string | null;
  biometricUrl: string | null;
  biometricRequestedAt: string | null;
  kycVerifiedAt: string | null;
}

const TERMINAL: ReadonlySet<string> = new Set([
  'DONE',
  'FAILED_OTP_TIMEOUT',
  'FAILED_OTP_REJECTED',
  'FAILED_KYC_TIMEOUT',
  'FAILED_KYC_REJECTED',
  'FAILED_CAPTCHA_TIMEOUT',
  'FAILED_PAYMENT',
  'FAILED_INFRA',
  'FAILED_MAX_ATTEMPTS',
  'FAILED_BROWSER_CRASH',
  'CANCELED',
]);

/** true si el status es final (no habrá más transiciones). */
export const isTerminal = (status: string): boolean => TERMINAL.has(status);

/** true si el status es un fallo (incluye CANCELED). */
export const isFailed = (status: string): boolean =>
  status.startsWith('FAILED_') || status === 'CANCELED';

/** Quita trailing slashes de la base del scrapper. */
const trimBase = (base: string): string => base.replace(/\/+$/, '');

export const eventsUrl = (base: string, paymentId: string): string =>
  `${trimBase(base)}/payments/${encodeURIComponent(paymentId)}/events`;

export const paymentUrl = (base: string, paymentId: string): string =>
  `${trimBase(base)}/payments/${encodeURIComponent(paymentId)}`;

/** URL WebSocket de signaling para publicar la cámara (http→ws, https→wss). */
export const signalWsUrl = (base: string, paymentId: string): string => {
  const wsBase = trimBase(base).replace(/^http/i, 'ws');
  return `${wsBase}/payments/${encodeURIComponent(paymentId)}/biometric/signal/cam`;
};

/**
 * Parser defensivo de un snapshot crudo (del SSE o del GET de fallback). Las
 * claves del backend son snake_case (Pydantic) y acá las mapeamos a camelCase.
 */
export const parseSnapshot = (raw: unknown): PaymentSnapshot | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.status !== 'string') return null;
  const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
  return {
    id: r.id,
    status: r.status,
    errorCode: str(r.error_code),
    errorMessage: str(r.error_message),
    biometricUrl: str(r.biometric_url),
    biometricRequestedAt: str(r.biometric_requested_at),
    kycVerifiedAt: str(r.kyc_verified_at),
  };
};

/** GET puntual del estado — fallback cuando el SSE no está disponible. */
export const fetchSnapshot = async (
  base: string,
  paymentId: string,
): Promise<PaymentSnapshot | null> => {
  const res = await fetch(paymentUrl(base, paymentId), {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return null;
  const json: unknown = await res.json();
  return parseSnapshot(json);
};
